<script setup lang="ts">
  import { computed } from 'vue';
  import { storeToRefs } from 'pinia';
  import { useNow } from '@vueuse/core';
  import { ChevronLeft, ChevronRight } from 'lucide-vue-next';
  import UploadIcon from '@/shared/assets/icons/UploadIcon.svg';
  import {
    ColoredButton,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
  } from '@/shared/ui';
  import { useDashboardStore } from '../model/dashboard.store';
  import {
    GRANULARITY_OPTIONS,
    getNextPeriod,
    getPreviousPeriod,
    isCurrentOrFuture,
    isMinOrPast,
    type Granularity,
  } from '../model/period';
  import { useDashboardExport } from '../model/useDashboardExport';
  import PeriodPicker from './PeriodPicker.vue';

  const dashboardStore = useDashboardStore();
  const { granularity, period, isLoading, data } = storeToRefs(dashboardStore);
  const now = useNow({ interval: 60_000 });
  const { isExporting, exportPdf } = useDashboardExport();

  const granularityLabel = computed(
    () => GRANULARITY_OPTIONS.find((option) => option.value === granularity.value)?.label ?? ''
  );

  const isNextPeriodDisabled = computed(() =>
    isCurrentOrFuture(granularity.value, period.value, now.value)
  );

  const isPreviousPeriodDisabled = computed(() => isMinOrPast(granularity.value, period.value));

  defineExpose({ isNextPeriodDisabled, isPreviousPeriodDisabled });

  function handleGranularityChange(value: unknown): void {
    if (isExporting.value) return;
    dashboardStore.setGranularity(value as Granularity);
  }

  function handlePeriodSelect(nextPeriod: string): void {
    if (isExporting.value) return;
    dashboardStore.setPeriod(nextPeriod);
  }

  function goToPreviousPeriod(): void {
    if (isPreviousPeriodDisabled.value || isExporting.value) return;
    dashboardStore.setPeriod(getPreviousPeriod(granularity.value, period.value));
  }

  function goToNextPeriod(): void {
    if (isNextPeriodDisabled.value || isExporting.value) return;
    dashboardStore.setPeriod(getNextPeriod(granularity.value, period.value));
  }
</script>

<template>
  <div
    data-testid="dashboard-filter-bar"
    class="flex items-center justify-between"
  >
    <div class="flex items-center gap-6">
      <Select
        :model-value="granularity"
        :disabled="isExporting"
        @update:model-value="handleGranularityChange"
      >
        <SelectTrigger
          class="w-31 justify-between rounded-lg border-gray-02 bg-white-01 px-4 text-sm font-medium text-gray-07 shadow-none data-[size=default]:h-[42px]"
        >
          <SelectValue>{{ granularityLabel }}</SelectValue>
        </SelectTrigger>
        <SelectContent>
          <SelectItem
            v-for="option in GRANULARITY_OPTIONS"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </SelectItem>
        </SelectContent>
      </Select>
      <div class="flex h-[42px] items-center gap-2 rounded-lg border border-gray-02 bg-white-01 px-4">
        <button
          type="button"
          aria-label="Período anterior"
          :disabled="isPreviousPeriodDisabled || isExporting"
          class="text-gray-07"
          :class="
            isPreviousPeriodDisabled || isExporting ? 'cursor-not-allowed opacity-40' : 'cursor-pointer'
          "
          @click="goToPreviousPeriod"
        >
          <ChevronLeft class="size-5" />
        </button>
        <PeriodPicker
          :granularity="granularity"
          :period="period"
          :disabled="isExporting"
          @select="handlePeriodSelect"
        />
        <button
          type="button"
          aria-label="Próximo período"
          :disabled="isNextPeriodDisabled || isExporting"
          class="text-gray-07"
          :class="isNextPeriodDisabled || isExporting ? 'cursor-not-allowed opacity-40' : 'cursor-pointer'"
          @click="goToNextPeriod"
        >
          <ChevronRight class="size-5" />
        </button>
      </div>
    </div>
    <ColoredButton
      text="Exportar"
      :icon="UploadIcon"
      icon-alt="Exportar"
      :loading="isExporting"
      :disabled="isExporting || isLoading || !data"
      @click="exportPdf"
    />
  </div>
</template>
